// iter68 — Universal "Print Current View" floating button.
// Snapshots the current <main> content, wraps it in a branded print template,
// and opens the print dialog. Mounts once in Layout — shows on every page.
//
// Pages that have a custom branded report (Variance, Genealogy, etc.) should
// keep using their dedicated "Print" button for full fidelity. This FAB is
// the fallback for screens that don't yet have one.
import React, { useState } from 'react';
import { Printer, X } from 'lucide-react';
import { Button } from './ui/button';
import { openBrandedPrintWindow, PrintHelpers as H } from '../utils/brandedPrint';
import { toast } from 'sonner';

const DEFAULT_HIDDEN_ROUTES = ['/login', '/inbox', '/tv'];

function shouldRenderFor(pathname) {
  if (!pathname) return true;
  return !DEFAULT_HIDDEN_ROUTES.some(r => pathname.startsWith(r));
}

function extractTitle() {
  const h1 = document.querySelector('main h1');
  if (h1?.innerText?.trim()) return h1.innerText.trim().toUpperCase();
  const docTitle = (document.title || '').replace(/\s*[–|·-]\s*(BOM Maestro|PeakPrairie).*$/i, '');
  return docTitle.toUpperCase() || 'PAGE PRINT';
}

function extractSubtitle() {
  const h1 = document.querySelector('main h1');
  const sub = h1?.parentElement?.querySelector('p');
  return sub?.innerText?.trim() || '';
}

// Strip elements that don't print well (buttons, dialogs, popovers, audio overlays, the FAB itself)
function cleanForPrint(rootHtml) {
  const wrapper = document.createElement('div');
  wrapper.innerHTML = rootHtml;
  const killSelectors = [
    'button', 'a[role="button"]', '[role="dialog"]', '[data-radix-popper-content-wrapper]',
    '[data-testid="voice-call-overlay"]', '[data-testid="voice-remote-audio"]',
    '[data-testid="incoming-page-banner"]', '[data-testid="print-current-view-fab"]',
    'input', 'select', 'textarea', '[data-testid$="-search"]',
  ];
  killSelectors.forEach(sel => wrapper.querySelectorAll(sel).forEach(el => el.remove()));
  return wrapper.innerHTML;
}

export default function PrintCurrentViewFab() {
  const [open, setOpen] = useState(false);
  const pathname = window.location.pathname;
  if (!shouldRenderFor(pathname)) return null;

  const printCurrent = async () => {
    try {
      const main = document.querySelector('main');
      if (!main) { toast.error('Nothing to print on this view'); return; }
      const html = cleanForPrint(main.innerHTML);
      const title = extractTitle();
      const subtitle = extractSubtitle();
      // Wrap raw page HTML with light typography normalization
      const body = `<div class="page-snapshot" style="font-size: 10pt;">
        ${html}
      </div>
      <style>
        .page-snapshot table { width: 100%; border-collapse: collapse; margin: 8pt 0; }
        .page-snapshot table th { background: #f3f4f6; text-align: left; padding: 5pt; font-size: 9pt; }
        .page-snapshot table td { padding: 4pt 5pt; border-bottom: 1px solid #e5e7eb; font-size: 9pt; }
        .page-snapshot svg, .page-snapshot canvas { max-width: 100%; height: auto; }
        .page-snapshot img { max-width: 100%; }
        .page-snapshot [data-state="active"] { font-weight: 600; }
      </style>
      ${H.signoffLines('Reviewed by', 'Date')}`;
      await openBrandedPrintWindow({
        title,
        subtitle,
        orientation: 'portrait',
        body,
        footerNote: `Snapshot of ${pathname} · ${new Date().toLocaleString()}`,
      });
      setOpen(false);
    } catch (e) {
      toast.error('Failed to prepare print view');
    }
  };

  return (
    <div className="fixed bottom-4 right-4 z-40 print:hidden" data-testid="print-current-view-fab">
      {open && (
        <div className="mb-2 bg-white border border-slate-300 shadow-2xl rounded-lg p-3 w-64">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-semibold">Print Current View</div>
            <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-700" data-testid="print-fab-close">
              <X className="w-4 h-4" />
            </button>
          </div>
          <p className="text-xs text-slate-500 mb-3">Generates a branded snapshot of whatever you're looking at — with logo, header, and footer.</p>
          <Button onClick={printCurrent} className="w-full" size="sm" data-testid="print-fab-snapshot-btn">
            <Printer className="w-4 h-4 mr-2" /> Print Snapshot
          </Button>
          <p className="text-[10px] text-slate-400 mt-2">For richer reports, use the dedicated "Print" button on Variance, Genealogy, Vendor Scorecard, Disposal, Warranty, BOMs.</p>
        </div>
      )}
      <button
        onClick={() => setOpen(o => !o)}
        title="Print this view"
        className="h-12 w-12 rounded-full bg-slate-900 hover:bg-slate-700 text-white shadow-lg flex items-center justify-center transition-transform hover:scale-110"
        data-testid="print-fab-trigger"
      >
        <Printer className="w-5 h-5" />
      </button>
    </div>
  );
}
